import { ActionConfirmation } from '@app/components/base/ActionConfirmation';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CheckboxElement, TextFieldElement, useFormContext } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { useDefaultTemplate } from '../hooks/useDefaultTemplate';
import { FormValues } from '../types';
import { AudiogramStyleFields } from './AudiogramStyleFields';
import { TemplatePreview } from './TemplatePreview';

interface Props {
  index: number;
  onRemove: () => void;
}

export function TemplatePanel({ index, onRemove }: Props) {
  const { $t, formatMessage } = useIntl();
  const { watch } = useFormContext<FormValues>();
  const { handleDefaultChange } = useDefaultTemplate();
  const templateName = watch(`templates.${index}.name`);
  const isDefault = watch(`templates.${index}.default`);

  return (
    <Accordion defaultExpanded={index === 0}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`template-content-${index}`}
        id={`template-header-${index}`}
      >
        <Stack direction="row" alignItems="center" spacing={2} width="100%">
          <Typography>
            {templateName ||
              $t({
                id: 'audiogram.newTemplate',
                defaultMessage: 'Nuevo template',
              })}
          </Typography>
          {isDefault && (
            <Chip
              label={$t({
                id: 'common.default',
                defaultMessage: 'Default',
              })}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <ActionConfirmation
            trigger={(onClick: (event: React.MouseEvent<HTMLElement>) => void) => (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick(e);
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            title={$t({
              id: 'audiogram.deleteTemplate',
              defaultMessage: 'Eliminar template',
            })}
            description={formatMessage(
              {
                id: 'audiogram.deleteTemplateConfirm',
                defaultMessage: 'Are you sure you want to delete the template "{name}"?',
              },
              {
                name:
                  templateName ||
                  $t({
                    id: 'audiogram.newTemplate',
                    defaultMessage: 'Nuevo template',
                  }),
              },
            )}
            onConfirm={onRemove}
            confirmText={$t({
              id: 'common.delete',
              defaultMessage: 'Eliminar',
            })}
            cancelText={$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
            icon="warning"
            iconColor="#ff7d55"
            placement="bottom-start"
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              <TextFieldElement
                label={$t({
                  id: 'audiogram.templateName',
                  defaultMessage: 'Nombre del template',
                })}
                fullWidth
                name={`templates.${index}.name`}
                required
              />
              <CheckboxElement
                name={`templates.${index}.default`}
                label={$t({
                  id: 'audiogram.defaultTemplate',
                  defaultMessage: 'Template por defecto',
                })}
                onChange={(e) => handleDefaultChange(index, e.target.checked)}
              />
              <AudiogramStyleFields fieldsPrefix={`templates.${index}.data.`} />
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                {$t({
                  id: 'common.preview',
                  defaultMessage: 'Preview',
                })}
              </Typography>
              <TemplatePreview index={index} />
            </Stack>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
